import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  Modal,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { useSelector } from "react-redux";
import { Postrequestcall } from "../../../../request/Postrequest";
import TimesheetSkeletonLoader from "../components/TimesheetSkeletonLoader";

import { FETCH_CLIENT_EMPLOYEE_FOR_TIMESHEET } from "../../../../constant/Apipath";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { ClientEnglishFallback } from "../../../constant/ClientEnglishFallback";

const ClientTimesheetEmployeesModal = ({
  isOpen,
  onClose,
  setSelectedEmp,
  empList,
  setSelectEmployees
}) => {
  const [clientEmployees, setEmployeesList] = useState([]);
  const [searchEmployee, setSearchEmployee] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedData, setSelectedData] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);

  const { loginData, selectedClientData, multiLanguageData } = useSelector((state) => state?.main);
  const CLIENT_ACCENT_COLOR = selectedClientData?.AccentColor;
  const TOKEN = loginData?.accessToken;

  const { TEXT_SELECT_EMPLOYEES, TEXT_SEARCH_EMPLOYEE, BUTTON_NEXT, TEXT_NO_EMPLOYEE_FOUND } =
    ClientEnglishFallback?.SelectJobEmployeesModal(
      multiLanguageData?.SelectJobEmployeesModal || {}
    );

  useEffect(() => {
    if (isOpen) {
      const initialList = empList || [];
      setSelectedData(initialList);
      setIsInitialized(true);
      getEmployee(initialList);
    } else {
      setIsInitialized(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && isInitialized) {
      getEmployee();
    }
  }, [searchEmployee]);

  const getEmployee = async (overrideSelectedData) => {
    try {
      setIsLoading(true);
      const payload = {
        ClientId: selectedClientData?.ClientId,
        PageNumber: 1,
        NrRowsPerPage: 100,
        SearchString: searchEmployee || ""
      };

      const clientJobRes = await Postrequestcall(FETCH_CLIENT_EMPLOYEE_FOR_TIMESHEET, payload, TOKEN);
      const data = clientJobRes?.data?.data || [];

      const currentSelected = overrideSelectedData !== undefined ? overrideSelectedData : (isInitialized ? selectedData : (empList || []));
      const selectedEmpIds = new Set(
        (currentSelected || []).map((item) => Number(item?.clientEmployeeId || item?.id || item?.ClientEmployeeId))
      );

      const processedEmployees = data?.map((employee) => {
        const empId = Number(employee?.id || employee?.clientEmployeeId || employee?.ClientEmployeeId);
        return {
          ...employee,
          checked: selectedEmpIds.has(empId)
        };
      });
      setEmployeesList(processedEmployees);
    } catch (error) {
      console.error("getEmployee Error: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Memoized filtered employees
  const filteredEmployees = useMemo(() => {
    const searchTerm = searchEmployee.toLowerCase().trim();
    if (!searchTerm) return clientEmployees;
    return clientEmployees?.filter(
      (employee) =>
        employee.firstName?.toLowerCase().includes(searchTerm) ||
        employee.lastName?.toLowerCase().includes(searchTerm)
    );
  }, [clientEmployees, searchEmployee]);

  const toggleEmployeeSelection = (employeeId) => {
    const targetEmpId = Number(employeeId);
    let toggledEmp = null;

    setEmployeesList((prev) =>
      prev?.map((employee) => {
        const empId = Number(employee?.id || employee?.clientEmployeeId || employee?.ClientEmployeeId);
        if (empId === targetEmpId) {
          toggledEmp = { ...employee, checked: !employee.checked };
          return toggledEmp;
        }
        return employee;
      })
    );

    setSelectedData((prevSelected) => {
      const exists = prevSelected.some((item) => {
        const itemId = Number(item?.clientEmployeeId || item?.id || item?.ClientEmployeeId);
        return itemId === targetEmpId;
      });

      if (exists) {
        return prevSelected.filter((item) => {
          const itemId = Number(item?.clientEmployeeId || item?.id || item?.ClientEmployeeId);
          return itemId !== targetEmpId;
        });
      } else if (toggledEmp) {
        return [
          ...prevSelected,
          {
            clientEmployeeId: toggledEmp.id || toggledEmp.clientEmployeeId,
            id: toggledEmp.id || toggledEmp.clientEmployeeId,
            firstName: toggledEmp.firstName,
            lastName: toggledEmp.lastName,
            clientEmployeeUDFieldsList: toggledEmp.clientEmployeeUDFieldsList,
            userProfilePicture: toggledEmp.userProfilePicture
          }
        ];
      }
      return prevSelected;
    });
  };

  const handleAddEmployees = () => {
    const formattedSelected = selectedData.map((employee) => ({
      clientEmployeeId: employee.clientEmployeeId || employee.id,
      id: employee.id || employee.clientEmployeeId,
      firstName: employee.firstName,
      lastName: employee.lastName,
      clientEmployeeUDFieldsList: employee.clientEmployeeUDFieldsList,
      userProfilePicture: employee.userProfilePicture
    }));

    setSelectedEmp(formattedSelected);
    setSelectEmployees(false);
    setSearchEmployee("");
  };

  const handleModalClose = () => {
    onClose();
    setSearchEmployee("");
  };

  const handleSearchChange = (e) => {
    let inputValue = e.target.value;
    inputValue = inputValue.replace(/^[^A-Za-z0-9]+/, "");
    setSearchEmployee(inputValue);
  };

  const hasSelectedEmployees = selectedData.length > 0;

  return (
    <Modal open={isOpen} onClose={handleModalClose}>
      <Box className="client-modal-responsive" sx={{ minHeight: "600px" }}>
        <Stack direction={"row"} sx={{ padding: "5px 0px", justifyContent: "space-between", alignItems: "center" }}>
          <IconButton
            size="small"
            onClick={() => {
              handleModalClose();
            }}>
            <ArrowBackIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              handleModalClose();
            }}
            style={{ padding: "4px" }}>
            <CloseIcon />
          </IconButton>
        </Stack>
        {isLoading ? (
          <Box sx={{ width: "100%" }}>
            <TimesheetSkeletonLoader variant="selectEmployee" />
          </Box>
        ) : (
          <>
            <Stack>
              <Typography variant="h6">{TEXT_SELECT_EMPLOYEES}</Typography>
            </Stack>
            <TextField
              fullWidth
              value={searchEmployee}
              size="small"
              placeholder={TEXT_SEARCH_EMPLOYEE}
              sx={{
                borderRadius: "10px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "0",
                  padding: "0"
                },
                "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                  border: "none"
                },
                backgroundColor: "#e4e4e4",
                marginTop: "10px"
              }}
              onChange={handleSearchChange}
            />
            <List
              dense
              sx={{
                width: "100%",
                overflow: "auto",
                gap: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: "430px",
                maxHeight: "430px",
                scrollbarWidth: "none"
              }}>
              {filteredEmployees?.map((employee) => (
                <ListItem
                  key={employee.id || employee.clientEmployeeId}
                  onClick={() => toggleEmployeeSelection(employee.id || employee.clientEmployeeId)}
                  sx={{
                    padding: "5px",
                    borderRadius: "10px",
                    border: `1px solid ${employee.checked ? CLIENT_ACCENT_COLOR : "#e4e4e4"}`,
                    margin: "5px 0px"
                  }}
                  disablePadding>
                  <Checkbox
                    sx={{
                      "&.Mui-checked": {
                        color: CLIENT_ACCENT_COLOR
                      }
                    }}
                    checked={employee.checked}
                  />
                  <ListItemAvatar>
                    <Avatar
                      sizes="small"
                      alt={`${employee.firstName} ${employee.lastName}`}
                      src={employee.userProfilePicture}
                    />
                  </ListItemAvatar>
                  <Typography variant="body2">{`${employee.firstName} ${employee.lastName}`}</Typography>
                </ListItem>
              ))}

              {filteredEmployees.length === 0 && (
                <Stack sx={{ textAlign: "center", justifyContent: "center", minHeight: "350px" }}>
                  <Typography variant="h5" style={{ fontSize: "18px" }}>
                    {TEXT_NO_EMPLOYEE_FOUND}
                  </Typography>
                </Stack>
              )}
            </List>
            <Button
              sx={{
                mt: 1,
                backgroundColor: hasSelectedEmployees ? CLIENT_ACCENT_COLOR : "lightgrey",
                color: "white"
              }}
              disabled={!hasSelectedEmployees}
              fullWidth
              onClick={handleAddEmployees}>
              {BUTTON_NEXT}{" "}
            </Button>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default ClientTimesheetEmployeesModal;
